/**
 * SpawnCostPreview Component
 *
 * User Story: US-P7-001B - Variable Cost Foundation
 * Step 4: Spawn Batch Form Updates
 *
 * Displays real-time cost preview for spawn batch creation.
 * Shows material breakdown with FIFO allocation estimates.
 *
 * Features:
 * - Real-time cost calculation as form values change
 * - Material breakdown table (grain, bags, filters)
 * - Warning states for unconfigured inventory items
 * - Loading and error states
 */

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import {
  AlertTriangle,
  CheckCircle2,
  Loader2,
  Package,
  PoundSterling,
} from 'lucide-react'
import { useSpawnBatchCostPreview } from '@/hooks/useSpawnBatchCost'

/**
 * Format a number as currency (GBP)
 */
function formatCurrency(value) {
  if (value === null || value === undefined) return '-'
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value)
}

/**
 * Format a number with appropriate precision
 */
function formatNumber(value, decimals = 2) {
  if (value === null || value === undefined) return '-'
  return new Intl.NumberFormat('en-GB', {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals,
  }).format(value)
}

/**
 * Single material row in the cost breakdown table
 */
function MaterialRow({ label, quantity, unit, cost, warning }) {
  return (
    <div className={`flex items-center justify-between py-2 text-sm ${warning ? 'text-amber-700' : ''}`}>
      <div className="flex items-center gap-2">
        {warning && <AlertTriangle className="h-3 w-3 text-amber-500" />}
        <span>{label}</span>
      </div>
      <div className="flex items-center gap-4">
        <span className="text-content-secondary w-16 text-right">
          {quantity ? `${formatNumber(quantity)} ${unit}` : '-'}
        </span>
        <span className="font-mono w-20 text-right">
          {formatCurrency(cost)}
        </span>
      </div>
    </div>
  )
}

/**
 * SpawnCostPreview Component
 *
 * @param {Object} props
 * @param {Object} props.formValues - Form values for cost calculation
 * @param {string} props.formValues.grain_type - Type of grain (rye, millet, etc.)
 * @param {number} props.formValues.bag_count - Number of spawn bags
 * @param {number} props.formValues.bag_weight_kg - Weight per bag in kg
 * @param {boolean} props.formValues.use_filters - Whether filter patches are used
 * @param {string} props.className - Additional CSS classes
 */
export default function SpawnCostPreview({ formValues, className = '' }) {
  const {
    costResult,
    isLoading,
    error,
    grainCost,
    bagCost,
    filterCost,
    totalCost,
    costPerBag,
    warnings,
  } = useSpawnBatchCostPreview(formValues, { debounceMs: 300 })

  // Extract form values for display
  const { grain_type, bag_count, bag_weight_kg, use_filters, grain_weight_per_bag_kg } = formValues || {}
  // Use actual grain weight from recipe, fallback to bag weight for legacy
  const effectiveGrainPerBag = grain_weight_per_bag_kg || bag_weight_kg
  const totalGrainWeight = bag_count && effectiveGrainPerBag ? bag_count * effectiveGrainPerBag : 0

  // Check if we have enough values to show preview
  const hasRequiredValues = grain_type && bag_count > 0 && bag_weight_kg > 0

  // If no required values yet, show placeholder
  if (!hasRequiredValues) {
    return (
      <Card className={className}>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <PoundSterling className="h-4 w-4" />
            Cost Preview
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-content-secondary">
            Configure bag count and grain type to see cost estimate.
          </p>
        </CardContent>
      </Card>
    )
  }

  // Loading state
  if (isLoading && !costResult) {
    return (
      <Card className={className}>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" />
            Calculating costs...
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <Skeleton className="h-6 w-full" />
            <Skeleton className="h-6 w-full" />
            <Skeleton className="h-6 w-full" />
          </div>
        </CardContent>
      </Card>
    )
  }

  // Error state
  if (error) {
    return (
      <Alert variant="destructive" className={className}>
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription>
          Failed to calculate cost: {error}
        </AlertDescription>
      </Alert>
    )
  }

  // Format grain type for display
  const grainLabel = grain_type
    ? grain_type.charAt(0).toUpperCase() + grain_type.slice(1)
    : 'Grain'

  // Format bag size for display
  const bagSizeLabel = bag_weight_kg ? `${bag_weight_kg}kg` : ''

  // Check for specific warnings
  const hasGrainWarning = warnings.some(w => w.toLowerCase().includes('grain'))
  const hasBagWarning = warnings.some(w => w.toLowerCase().includes('bag'))
  const hasFilterWarning = use_filters && warnings.some(w => w.toLowerCase().includes('filter'))

  return (
    <Card className={className}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Package className="h-4 w-4" />
            Cost Preview
          </CardTitle>
          {warnings.length === 0 ? (
            <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
              <CheckCircle2 className="h-3 w-3 mr-1" />
              Inventory configured
            </Badge>
          ) : (
            <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
              <AlertTriangle className="h-3 w-3 mr-1" />
              {warnings.length} warning{warnings.length !== 1 ? 's' : ''}
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Warnings alert */}
        {warnings.length > 0 && (
          <Alert className="bg-amber-50 border-amber-200">
            <AlertTriangle className="h-4 w-4 text-amber-600" />
            <AlertDescription className="text-amber-800">
              <span className="font-medium">Missing inventory configuration</span>
              <ul className="mt-1 text-sm list-disc list-inside">
                {warnings.map((warning, idx) => (
                  <li key={idx}>{warning}</li>
                ))}
              </ul>
            </AlertDescription>
          </Alert>
        )}

        {/* Material breakdown table */}
        <div className="divide-y">
          <div className="flex items-center justify-between py-2 text-xs text-content-tertiary uppercase tracking-wide">
            <span>Material</span>
            <div className="flex items-center gap-4">
              <span className="w-16 text-right">Qty</span>
              <span className="w-20 text-right">Cost</span>
            </div>
          </div>

          <MaterialRow
            label={`${grainLabel} (grain)`}
            quantity={totalGrainWeight}
            unit="kg"
            cost={grainCost}
            warning={hasGrainWarning}
          />

          <MaterialRow
            label={`Spawn Bag ${bagSizeLabel}`}
            quantity={bag_count}
            unit=""
            cost={bagCost}
            warning={hasBagWarning}
          />

          {use_filters && (
            <MaterialRow
              label="Filter patches"
              quantity={bag_count}
              unit=""
              cost={filterCost}
              warning={hasFilterWarning}
            />
          )}
        </div>

        {/* Totals section */}
        <div className="pt-3 border-t space-y-2">
          <div className="flex items-center justify-between">
            <span className="font-medium">Total</span>
            <span className="text-lg font-bold">{formatCurrency(totalCost)}</span>
          </div>
          <div className="flex items-center justify-between text-sm text-content-secondary">
            <span>Cost per bag</span>
            <span className="font-mono">{formatCurrency(costPerBag)}</span>
          </div>
        </div>

        {/* Loading indicator for updates */}
        {isLoading && costResult && (
          <div className="flex items-center justify-center text-xs text-content-tertiary">
            <Loader2 className="h-3 w-3 animate-spin mr-1" />
            Updating...
          </div>
        )}
      </CardContent>
    </Card>
  )
}
